import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  ShoppingBag, Store, Boxes, Users, Wallet, LineChart, Megaphone, CreditCard,
  Check, ArrowRight, Loader2, Menu, X, Sparkles, ScanLine, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import VisionLogo from "@/components/shared/VisionLogo";

const HERO = "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=1600&q=80";

const FEATURES = [
  { icon: Receipt, title: "Caisse / POS", desc: "Encaissez en boutique en quelques secondes, gestion des variantes et reçus." },
  { icon: Store, title: "Boutique en ligne", desc: "Chaque commerce dispose de sa boutique e-commerce personnalisable." },
  { icon: Boxes, title: "Gestion des stocks", desc: "Suivi par couleur, taille et quantité avec alertes de stock faible." },
  { icon: Users, title: "Clients & CRM", desc: "Fidélisez et suivez vos prospects avec un pipeline commercial clair." },
  { icon: Megaphone, title: "Marketing", desc: "Campagnes email, WhatsApp et SMS segmentées vers vos clients." },
  { icon: Wallet, title: "Trésorerie", desc: "Caisses, mobile money, banque : suivez chaque encaissement." },
  { icon: CreditCard, title: "Charges", desc: "Enregistrez vos charges et transformez-les en décaissements." },
  { icon: LineChart, title: "Analyse", desc: "CA, panier moyen, rentabilité et top produits en temps réel." },
];

export default function Marketing() {
  const [plans, setPlans] = useState([]);
  const [menu, setMenu] = useState(false);

  useEffect(() => { api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  return (
    <div className="vp-dark min-h-screen">
      {/* NAV */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><VisionLogo variant="dark" size={32} /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
            <a href="#features" className="hover:text-white transition-colors" data-testid="nav-link-features">Fonctionnalités</a>
            <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-link-pricing">Tarifs</a>
            <a href="#contact" className="hover:text-white transition-colors" data-testid="nav-link-contact">Contact</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" className="text-slate-200 hover:text-white hover:bg-white/5" data-testid="nav-login-btn">Connexion</Button></Link>
            <Link to="/register"><Button className="vp-accent-bg text-black font-medium hover:opacity-90" data-testid="nav-register-btn">Créer ma boutique</Button></Link>
          </div>
          <button className="md:hidden text-white" onClick={() => setMenu(!menu)} data-testid="nav-mobile-toggle">{menu ? <X /> : <Menu />}</button>
        </div>
        {menu && (
          <div className="md:hidden border-t border-white/10 px-5 py-4 flex flex-col gap-3 text-slate-200">
            <a href="#features" onClick={() => setMenu(false)}>Fonctionnalités</a>
            <a href="#pricing" onClick={() => setMenu(false)}>Tarifs</a>
            <a href="#contact" onClick={() => setMenu(false)}>Contact</a>
            <Link to="/login"><Button variant="outline" className="w-full border-white/20 text-white bg-transparent">Connexion</Button></Link>
            <Link to="/register"><Button className="w-full vp-accent-bg text-black">Créer ma boutique</Button></Link>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="boutique de mode" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,12,16,0.6) 0%, rgba(11,12,16,0.95) 80%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <span className="overline-label vp-accent-text inline-flex items-center gap-2"><Sparkles size={14} /> Solution SaaS pour boutiques de mode</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mt-4 leading-[1.05]">
              La solution complète pour piloter votre boutique de vêtements.
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mt-6 max-w-2xl leading-relaxed">
              Vendez en boutique et en ligne, gérez votre stock, vos clients, votre trésorerie et analysez vos performances — le tout depuis une seule plateforme.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Link to="/register"><Button size="lg" className="vp-accent-bg text-black font-semibold hover:opacity-90 hover-lift" data-testid="hero-cta-register">Démarrer gratuitement <ArrowRight size={18} className="ml-1" /></Button></Link>
              <DemoDialog />
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-10 text-sm text-slate-400">
              {["Vendre en boutique", "Vendre en ligne", "Gérer son stock", "Suivre sa trésorerie", "Analyser ses performances"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Check size={15} className="vp-accent-text" /> {t}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-5 py-20 lg:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="overline-label vp-accent-text">Fonctionnalités</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3">Tout ce qu'il faut pour développer votre commerce</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
              className="vp-card rounded-xl p-6 hover-lift hover:border-[var(--vp-accent)]/40">
              <div className="h-11 w-11 rounded-lg grid place-items-center mb-4" style={{ background: "rgba(61,187,44,0.12)" }}><f.icon size={20} className="vp-accent-text" /></div>
              <h3 className="text-lg font-semibold text-white">{f.title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-5 py-20 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="overline-label vp-accent-text">Tarifs</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3">Des formules adaptées à votre croissance</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((p) => (
            <div key={p.id} data-testid="pricing-plan"
              className={`vp-card rounded-2xl p-7 hover-lift relative ${p.featured ? "border-[var(--vp-accent)]" : ""}`}>
              {p.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 vp-accent-bg text-black text-xs font-semibold px-3 py-1 rounded-full">Populaire</span>}
              <h3 className="font-display text-2xl font-bold text-white">{p.name}</h3>
              <div className="mt-4 mb-6"><span className="text-4xl font-bold text-white mono">{fmtMoney(p.price)}</span><span className="text-slate-400 text-sm"> / {p.period}</span></div>
              <ul className="space-y-3 mb-7">
                {(p.features || []).map((ft) => <li key={ft} className="flex items-start gap-2 text-sm text-slate-300"><Check size={16} className="vp-accent-text mt-0.5 shrink-0" /> {ft}</li>)}
              </ul>
              <Link to="/register"><Button className={`w-full font-medium ${p.featured ? "vp-accent-bg text-black" : "bg-white/10 text-white hover:bg-white/20"}`}>Choisir {p.name}</Button></Link>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="max-w-7xl mx-auto px-5 py-20">
        <div className="vp-card rounded-2xl p-8 lg:p-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="overline-label vp-accent-text">Contact</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">Une question ? Parlons-en.</h2>
            <p className="text-slate-400 leading-relaxed mb-6">Notre équipe vous accompagne dans la digitalisation de votre boutique de mode.</p>
            <div className="space-y-2 text-sm text-slate-300">
              <p>📧 contact@vilopos.com</p>
              <p>📞 +228 90 00 00 00</p>
              <p>📍 Lomé, Togo</p>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <VisionLogo variant="dark" size={26} />
          <p className="text-sm text-slate-500">© 2026 Vision POS. Tous droits réservés.</p>
          <Link to="/admin" className="text-xs text-slate-600 hover:text-slate-400" data-testid="footer-admin-link">Espace administration</Link>
        </div>
      </footer>
    </div>
  );
}

function DemoDialog() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ nom: "", prenom: "", telephone: "", whatsapp: "", email: "", boutique: "", ville_pays: "", points_vente: "", besoin: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setLoading(true);
    try { await api.post("/public/demo-request", f); toast.success("Demande envoyée ! Nous vous contacterons rapidement."); setOpen(false); }
    catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="lg" variant="outline" className="border-white/20 text-white bg-transparent hover:bg-white/5" data-testid="hero-demo-btn">Demander une démonstration</Button></DialogTrigger>
      <DialogContent className="vp-card border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Demander une démonstration</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <Input placeholder="Prénom" value={f.prenom} onChange={set("prenom")} className="bg-black/30 border-white/10" data-testid="demo-prenom" />
          <Input placeholder="Nom" value={f.nom} onChange={set("nom")} className="bg-black/30 border-white/10" data-testid="demo-nom" />
          <Input placeholder="Téléphone" value={f.telephone} onChange={set("telephone")} className="bg-black/30 border-white/10" />
          <Input placeholder="WhatsApp" value={f.whatsapp} onChange={set("whatsapp")} className="bg-black/30 border-white/10" />
          <Input placeholder="Email" value={f.email} onChange={set("email")} className="bg-black/30 border-white/10 col-span-2" data-testid="demo-email" />
          <Input placeholder="Nom de la boutique" value={f.boutique} onChange={set("boutique")} className="bg-black/30 border-white/10" />
          <Input placeholder="Ville / Pays" value={f.ville_pays} onChange={set("ville_pays")} className="bg-black/30 border-white/10" />
          <Input placeholder="Nombre de points de vente" value={f.points_vente} onChange={set("points_vente")} className="bg-black/30 border-white/10 col-span-2" />
          <Textarea placeholder="Votre besoin principal / message" value={f.message} onChange={set("message")} className="bg-black/30 border-white/10 col-span-2" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} className="vp-accent-bg text-black font-medium" data-testid="demo-submit">{loading && <Loader2 className="animate-spin mr-2" size={15} />} Envoyer la demande</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactForm() {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/public/contact", f); toast.success("Message envoyé !"); setF({ name: "", email: "", subject: "", message: "" }); }
    catch (e2) { toast.error(apiError(e2)); }
    setLoading(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label className="text-slate-300 text-sm mb-1.5 block">Nom</Label><Input value={f.name} onChange={set("name")} required className="bg-black/30 border-white/10 text-white" data-testid="contact-name" /></div>
        <div><Label className="text-slate-300 text-sm mb-1.5 block">Email</Label><Input value={f.email} onChange={set("email")} type="email" required className="bg-black/30 border-white/10 text-white" data-testid="contact-email" /></div>
      </div>
      <div><Label className="text-slate-300 text-sm mb-1.5 block">Sujet</Label><Input value={f.subject} onChange={set("subject")} className="bg-black/30 border-white/10 text-white" /></div>
      <div><Label className="text-slate-300 text-sm mb-1.5 block">Message</Label><Textarea value={f.message} onChange={set("message")} required className="bg-black/30 border-white/10 text-white" /></div>
      <Button type="submit" disabled={loading} className="vp-accent-bg text-black font-medium" data-testid="contact-submit">{loading && <Loader2 className="animate-spin mr-2" size={15} />} Envoyer</Button>
    </form>
  );
}
